import React, { useState, useMemo } from 'react';
import { MINIGAME_LIST, getGame, mod, newSeed } from '../../lib/minigames';

// Compose: pick recipient + game, see stat preview, send proposal.
// Review:  read-only matchup view, Decline/Accept buttons (or Cancel if sender).

const DEFAULT_AS = { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };
const STAT_LABEL = { str: 'STR', dex: 'DEX', con: 'CON', int: 'INT', wis: 'WIS', cha: 'CHA' };

export default function ChallengePanel({
  mode,
  myPlayerId,
  players,
  challenge,            // existing challenge record (review mode)
  onClose,
  onSend,               // (record) => Promise — compose
  onAccept,             // () => Promise        — review (recipient)
  onDecline,            // () => Promise        — review (recipient)
  onCancel,             // () => Promise        — review (sender)
}) {
  const isCompose = mode === 'compose';
  const isReview  = mode === 'review';

  const otherPlayers = useMemo(
    () => Object.values(players || {}).filter((p) => p.id !== myPlayerId && p.charName),
    [players, myPlayerId],
  );

  const [recipientId, setRecipientId] = useState(otherPlayers[0]?.id || '');
  const [gameId,      setGameId]      = useState(MINIGAME_LIST[0]?.id || '');

  const sender    = isCompose ? players[myPlayerId]   : players[challenge?.fromPlayerId];
  const recipient = isCompose ? players[recipientId]  : players[challenge?.toPlayerId];
  const game      = isCompose ? getGame(gameId)       : getGame(challenge?.gameId);

  const senderAS    = { ...DEFAULT_AS, ...(sender?.abilityScores    || {}) };
  const recipientAS = { ...DEFAULT_AS, ...(recipient?.abilityScores || {}) };

  // Resolve the relevant stat scores for whichever game is selected
  const senderStat    = game ? (senderAS[game.stat]    || 10) : 10;
  const recipientStat = game ? (recipientAS[game.stat] || 10) : 10;
  const senderMod     = mod(senderStat);
  const recipientMod  = mod(recipientStat);
  const diff          = senderMod - recipientMod; // positive = sender has the edge

  // Edge description for the compose preview
  const edgeText =
    !game ? '' :
    diff === 0 ? 'Even matchup.' :
    diff > 0   ? `You have +${diff} ${STAT_LABEL[game.stat]} advantage.` :
                 `Opponent has +${-diff} ${STAT_LABEL[game.stat]} advantage.`;

  const canSend = isCompose && !!recipient && !!game;

  const send = async () => {
    if (!canSend) return;
    const seed       = newSeed();
    const fromParams = game.paramsFromStats(senderStat,    recipientStat);
    const toParams   = game.paramsFromStats(recipientStat, senderStat);
    const record = {
      fromPlayerId: sender.id,
      toPlayerId:   recipient.id,
      fromName:     sender.charName || 'Player',
      toName:       recipient.charName || 'Player',
      gameId:       game.id,
      stat:         game.stat,
      fromStat:     senderStat,
      toStat:       recipientStat,
      seed,
      fromParams,
      toParams,
      status:       'pending',
      fromScore:    null,
      toScore:      null,
      winner:       null,
      createdAt:    Date.now(),
      resolvedAt:   null,
    };
    await onSend?.(record);
  };

  const viewerIsSender = isReview && challenge?.fromPlayerId === myPlayerId;

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>
            🎯 {isCompose ? 'Challenge Another Player' : 'Challenge'}
          </span>
          {onClose && <button style={styles.closeBtn} onClick={onClose}>✕</button>}
        </div>

        {isCompose && (
          <>
            <div style={styles.row}>
              <span style={styles.rowLabel}>Opponent</span>
              <select
                style={styles.select}
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
              >
                {otherPlayers.length === 0 && <option value="">(no other players)</option>}
                {otherPlayers.map((p) => (
                  <option key={p.id} value={p.id}>{p.charName}</option>
                ))}
              </select>
            </div>

            <div style={styles.gameGrid}>
              {MINIGAME_LIST.map((g) => (
                <button
                  key={g.id}
                  style={{ ...styles.gameCard, ...(gameId === g.id ? styles.gameCardActive : {}) }}
                  onClick={() => setGameId(g.id)}
                >
                  <span style={styles.gameIcon}>{g.icon}</span>
                  <span style={styles.gameLabel}>{g.label}</span>
                  <span style={styles.gameStat}>{STAT_LABEL[g.stat]}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Matchup preview — shown in both modes */}
        {game && sender && recipient && (
          <div style={styles.matchup}>
            <PlayerStatBlock
              player={sender}
              stat={game.stat}
              statValue={senderStat}
              statMod={senderMod}
              isMe={viewerIsSender || isCompose}
            />
            <div style={styles.vs}>VS</div>
            <PlayerStatBlock
              player={recipient}
              stat={game.stat}
              statValue={recipientStat}
              statMod={recipientMod}
              isMe={isReview && !viewerIsSender}
            />
          </div>
        )}

        {game && (
          <div style={styles.gameDesc}>
            <div style={styles.gameDescLabel}>{game.label}</div>
            <div style={styles.gameDescText}>{game.description}</div>
            <div style={styles.edge}>{edgeText}</div>
          </div>
        )}

        {/* Action bar */}
        <div style={styles.actions}>
          {isCompose && (
            <>
              <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
              <button style={styles.sendBtn} onClick={send} disabled={!canSend}>
                Send challenge
              </button>
            </>
          )}
          {isReview && challenge?.status === 'pending' && viewerIsSender && (
            <>
              <button style={styles.cancelBtn} onClick={onClose}>Close</button>
              <button style={styles.declineBtn} onClick={onCancel}>Cancel proposal</button>
            </>
          )}
          {isReview && challenge?.status === 'pending' && !viewerIsSender && (
            <>
              <button style={styles.declineBtn} onClick={onDecline}>Decline</button>
              <button style={styles.sendBtn} onClick={onAccept}>Accept</button>
            </>
          )}
          {isReview && challenge?.status !== 'pending' && (
            <button style={styles.cancelBtn} onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayerStatBlock({ player, stat, statValue, statMod, isMe }) {
  return (
    <div style={{ ...styles.statBlock, ...(isMe ? styles.statBlockMe : {}) }}>
      <span style={{ ...styles.statDot, background: player?.colour || '#9a8b6e' }} />
      <span style={styles.statName}>{player?.charName || '—'}{isMe ? ' (you)' : ''}</span>
      <span style={styles.statRow}>
        <span style={styles.statLabel}>{STAT_LABEL[stat]}</span>
        <span style={styles.statValue}>{statValue}</span>
        <span style={styles.statMod}>{statMod >= 0 ? `+${statMod}` : statMod}</span>
      </span>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  panel:   { width: '100%', maxWidth: 480, maxHeight: '92vh', background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', overflow: 'hidden', color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #4a3d28', background: 'rgba(0,0,0,0.2)' },
  title:   { fontSize: 16, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em' },
  closeBtn:{ background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 20, cursor: 'pointer', padding: '0 4px' },

  row:        { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid rgba(74,61,40,0.4)' },
  rowLabel:   { fontSize: 11, color: '#9a8b6e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
  select:     { flex: 1, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '6px 8px', fontSize: 13, outline: 'none', fontFamily: 'inherit' },

  gameGrid:    { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, padding: '10px 14px' },
  gameCard:    { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '10px 6px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  gameCardActive: { borderColor: '#d4a847', background: 'rgba(212,168,71,0.1)', color: '#d4a847', boxShadow: '0 0 10px rgba(212,168,71,0.3)' },
  gameIcon:    { fontSize: 22, lineHeight: 1 },
  gameLabel:   { fontSize: 12, fontWeight: 700, letterSpacing: '0.04em' },
  gameStat:    { fontSize: 9, color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.06em' },

  matchup:     { display: 'flex', alignItems: 'stretch', gap: 6, padding: '10px 14px' },
  statBlock:   { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 8, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6 },
  statBlockMe: { borderColor: '#d4a847', background: 'rgba(212,168,71,0.06)' },
  statDot:     { width: 10, height: 10, borderRadius: '50%' },
  statName:    { fontSize: 12, fontWeight: 700, color: '#f0e6d0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' },
  statRow:     { display: 'flex', alignItems: 'baseline', gap: 4 },
  statLabel:   { fontSize: 9, color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.06em' },
  statValue:   { fontSize: 22, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace" },
  statMod:     { fontSize: 11, color: '#7ab8e0', fontWeight: 700 },
  vs:          { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#9a8b6e', padding: '0 4px', letterSpacing: '0.1em' },

  gameDesc:    { padding: '10px 14px', borderTop: '1px solid rgba(74,61,40,0.5)', borderBottom: '1px solid rgba(74,61,40,0.5)', display: 'flex', flexDirection: 'column', gap: 4 },
  gameDescLabel: { fontSize: 12, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em' },
  gameDescText:{ fontSize: 12, color: '#9a8b6e', lineHeight: 1.4 },
  edge:        { fontSize: 12, color: '#7ab8e0', fontWeight: 700 },

  actions:   { display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '10px 14px', background: 'rgba(0,0,0,0.18)' },
  cancelBtn: { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', padding: '6px 14px', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  declineBtn:{ background: 'rgba(192,51,51,0.18)', border: '1px solid rgba(192,51,51,0.5)', borderRadius: 4, color: '#f87171', padding: '6px 14px', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  sendBtn:   { background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '1px solid #c4a44a', color: '#1a0e00', padding: '6px 16px', fontSize: 13, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4 },
};
